import React, { createContext, useContext, useEffect, useState } from 'react';
import supabase from '../supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRow, setUserRow] = useState(null); // Data for regular users
  const [business, setBusiness] = useState(null); // Data for business users
  const [subscription, setSubscription] = useState(null); 
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const isBusiness = !!business;

  const hasActiveAccess = () => {
    if (!subscription) return false;
    if (subscription.status === 'active') return true;
    if (subscription.status === 'trialing') {
      return new Date(subscription.next_bill_date) > new Date();
    }
    return false;
  };

  const updateWidgetSettings = async (settings) => {
    if (!business?.id) return { error: 'No business found' };
    try {
      const { data, error } = await supabase
        .from('businesses')
        .update({
          widget_color: settings.color,
          is_dark_mode: settings.darkMode,
          has_shadow: settings.shadow
        })
        .eq('id', business.id)
        .select()
        .single();

      if (error) throw error;
      setBusiness(data);
      return { success: true };
    } catch (err) {
      console.error("Update Settings Error:", err);
      return { error: err.message };
    }
  };

  useEffect(() => {
    let subSubscription = null;

    const syncAuth = async (session) => {
      const currentUser = session?.user || null;
      
      if (!currentUser) {
        setUser(null);
        setUserRow(null);
        setBusiness(null);
        setSubscription(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setUser(currentUser);

      try {
        // Parallel fetch for all possible roles/data
        const [bizRes, adminRes, subRes, userRes] = await Promise.all([
          supabase.from('businesses').select('*').eq('id', currentUser.id).maybeSingle(),
          supabase.from('admin_users').select('user_id').eq('user_id', currentUser.id).maybeSingle(),
          supabase.from('subscriptions').select('*').eq('business_id', currentUser.id).maybeSingle(),
          supabase.from('users').select('*').eq('id', currentUser.id).maybeSingle() // Fetch regular user data
        ]);

        setIsAdmin(!!adminRes?.data);
        setBusiness(bizRes?.data || null);
        setSubscription(subRes?.data || null);
        setUserRow(userRes?.data || null); // This fixes the Header undefined error

        // Realtime Subscription Listener
        if (subSubscription) supabase.removeChannel(subSubscription);

        subSubscription = supabase
          .channel(`public:subscriptions:${currentUser.id}`)
          .on('postgres_changes', 
            { 
              event: '*', 
              schema: 'public', 
              table: 'subscriptions', 
              filter: `business_id=eq.${currentUser.id}` 
            }, 
            (payload) => {
              setSubscription(payload.new || null);
            }
          )
          .subscribe();

      } catch (err) {
        console.error("Auth Sync Error:", err);
      } finally {
        setLoading(false);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      syncAuth(session);
    });

    const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange((_event, session) => {
      syncAuth(session);
    });

    return () => {
      authListener.unsubscribe();
      if (subSubscription) supabase.removeChannel(subSubscription);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      userRow, // Provided to Header/Userdiv
      business, 
      isBusiness, 
      isAdmin, 
      loading, 
      setBusiness,
      subscription,          
      setSubscription,
      hasActiveAccess: hasActiveAccess(),
      updateWidgetSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);